import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import { Button, Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import {
  EstateResultItem,
  EstateResultItem_AccountInput,
  EstateResultItem_OneViewAddressInput,
  EstateResultItem_OneViewBankruptcyInput,
  EstateResultItem_OneViewCollectionInput,
  EstateResultItem_OneViewEmploymentInput,
  EstateResultItem_OneViewNameInput,
  EstateResultItem_OneViewSourceInfoInput,
  EstateResultItem_OneViewTradeInput,
  EstateResultItem_PropertyReportInput,
  EstateResultItem_TheWorkNumberTaxTranscriptInput,
  EstateResultItemType,
  EstateResultSource,
  PortalPermissions,
  useEstate_GetEstateByIdQuery,
  useEstateResult_GetEstateResultByEstateIdQuery,
  useEstateResultItem_GetByEstateIdQuery,
  useEstateShareMembership_GetEstateShareMembershipQuery,
} from 'queries';

import Spinner from 'common/spinner';
import HomeLayout from 'layouts/home-layout';
import CreatePaymentModal from 'pages/common/estate/create-payment-modal';
import { EstateDashboardTab } from 'pages/estate-dashboard/estate-dashboard';
import AddOrEditResultItemModal from 'pages/superadmin-estates/components/add-or-edit-result-item-modal';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';
import useHasPortalPermission from 'utils/useHasPortalPermission';

import AddResultItemButton from '../superadmin-estates/components/add-result-item-button';

import AccountAsset from './components/account-asset';
import AddressAsset from './components/address-asset';
import AssetCard from './components/asset-card';
import BankruptcyAsset from './components/bankruptcy-asset';
import CollectionAsset from './components/collection-asset';
import EmploymentAsset from './components/employment-asset';
import EstateResultItemPaymentForm from './components/estate-result-item-payment-form';
import NameAsset from './components/name-asset';
import PropertyAsset from './components/property-asset';
import SourceAsset from './components/source-asset';
import TradeAsset from './components/trade-asset';
import TranscriptAsset from './components/transcript-asset';
import TwnMessagePanel from './components/twn-message-panel';
import {
  EstateResultItemState,
  EstateResultsPageContext,
} from './context/estate-results-page-context';
import { sources } from './utils/additional-sources';
import { fixupTrades } from './utils/process-result-items';

const useStyles = makeStyles((theme) => ({
  parentContainer: {
    margin: 'auto',
    marginTop: '50px',
    height: '100%',
  },
  parentRoute: {
    color: '#8DA8B2',
    cursor: 'pointer',
  },
  routeIconContainer: {
    verticalAlign: 'middle',
  },
  container: {
    padding: '5%',
    flexGrow: 1,
  },
  chatButton: {
    position: 'fixed',
    right: 40,
    bottom: 150,
  },
  estateDashboardButton: {
    marginLeft: '16px',
    backgroundColor: '#F9F9FB',
    color: '#1C2541',
    borderRadius: '8px',
    fontSize: '14px',
    boxShadow: 'none',
  },
  estateDashboardButtonIcon: {
    marginRight: '8px',
  },
  gridContainer: {
    marginTop: 10,
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
    paddingBottom: '50px',
  },
  gridRight: {
    float: 'left',
    maxWidth: '95%',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  gridCenter: {
    float: 'left',
    maxWidth: '95%',
    marginRight: 5,
    [theme.breakpoints.down('md')]: {
      width: '100%',
      marginBottom: 8,
    },
  },
  gridLeft: {
    float: 'left',
    maxWidth: '95%',
    marginRight: 5,
    [theme.breakpoints.down('md')]: {
      width: '100%',
      marginBottom: 8,
    },
  },
  reportDate: {
    fontStyle: 'italic',
  },
  sublistHeader: {
    fontStyle: 'italic',
    marginLeft: 10,
    marginRight: 10,
    borderBottom: '1px black solid',
  },
  noData: {
    fontStyle: 'italic',
    marginLeft: 10,
    opacity: 0.5,
    borderBottom: 'none',
  },
}));

interface Props {
  hideNav?: boolean;
}

export default function EstateResultsPage({ hideNav = false }: Props) {
  const classes = useStyles();
  const history = useHistory();
  const { portalId, estateId } = useParams<Record<string, string>>();
  const hasPortalPermissionAdmin = useHasPortalPermission(PortalPermissions.Admin);
  const hasPortalPermissionUser = useHasPortalPermission(PortalPermissions.User);
  const hasPortalPermissionClient = useHasPortalPermission(PortalPermissions.Client);

  const [currentEstateResultItem, setCurrentEstateResultItem] = useState<
    Partial<EstateResultItem> | undefined
  >();
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const [showCreateResultItemModal, setShowCreateResultItemModal] = useState(false);
  const [nameData, setNameData] = useState<Array<Partial<EstateResultItem>>>([]);
  const [employmentData, setEmploymentData] = useState<Array<Partial<EstateResultItem>>>([]);
  const [homeAddressData, setHomeAddressData] = useState<Array<Partial<EstateResultItem>>>([]);
  const [propertyData, setPropertyData] = useState<Array<Partial<EstateResultItem>>>([]);
  const [bankruptcyData, setBankruptcyData] = useState<Array<Partial<EstateResultItem>>>([]);
  const [collectionData, setCollectionData] = useState<Array<Partial<EstateResultItem>>>([]);
  const [sourceData, setSourceData] = useState<Array<Partial<EstateResultItem>>>([]);
  const [openTradeData, setOpenTradeData] = useState<Array<Partial<EstateResultItem>>>([]);
  const [closedTradeData, setClosedTradeData] = useState<Array<Partial<EstateResultItem>>>([]);
  const [suspectedAccountData, setSuspectedAccountData] = useState<
    Array<Partial<EstateResultItem>>
  >([]);
  const [confirmedAccountData, setConfirmedAccountData] = useState<
    Array<Partial<EstateResultItem>>
  >([]);

  const [context, setContext] = useState<EstateResultItemState>({
    isEditingEstateResultItem: false,
    estateResultItemIdToEdit: null,
    setContext: () => {},
  });

  const [taxTranscriptData, setTaxTranscriptData] = useState<Array<Partial<EstateResultItem>>>([]);
  const [taxTranscriptEmploymentData, setTaxTranscriptEmploymentData] = useState<
    Array<Partial<EstateResultItem>>
  >([]);

  const { data: estateData, loading: estateLoading } = useEstate_GetEstateByIdQuery({
    variables: { id: estateId },
    onError: (e) => {
      showAlert({
        message: 'Error getting estate by ID: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const { data: estateResultData, loading: estateResultLoading } =
    useEstateResult_GetEstateResultByEstateIdQuery({
      variables: {
        input: {
          portalId,
          estateId,
        },
      },
      onError: (e) => console.log('Error getting estateResult by estateId: ' + e.message),
    });

  const {
    data: estateResultItemData,
    loading: estateResultItemsLoading,
    refetch: refetchEstateResultItems,
  } = useEstateResultItem_GetByEstateIdQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: { estateId: estateId, filter: { visible: true } },
    onError: (e) => {
      showAlert({
        message: 'Error getting estate result item by estate ID: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const { data: estateShareMembershipData, loading: estateShareMembershipLoading } =
    useEstateShareMembership_GetEstateShareMembershipQuery({
      variables: { input: { estateId, portalId } },
      onError: (e) => {
        showAlert({
          message: 'Error getting estate share membership: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const handleAddResultItemClick = () => {
    setShowCreateResultItemModal(true);
  };

  useEffect(() => {
    if (estateResultItemData) {
      setNameData(
        estateResultItemData?.data.filter((item) => item.type === EstateResultItemType.Name) || [],
      );
      setEmploymentData(
        estateResultItemData?.data.filter(
          (item) => item.type === EstateResultItemType.Employment,
        ) || [],
      );
      setHomeAddressData(
        estateResultItemData?.data.filter((item) => item.type === EstateResultItemType.Address) ||
          [],
      );
      setPropertyData(
        estateResultItemData?.data.filter((item) => item.type === EstateResultItemType.Property) ||
          [],
      );
      setBankruptcyData(
        estateResultItemData?.data.filter(
          (item) => item.type === EstateResultItemType.Bankruptcy,
        ) || [],
      );
      setCollectionData(
        estateResultItemData?.data.filter(
          (item) => item.type === EstateResultItemType.Collection,
        ) || [],
      );
      setSourceData(
        sources.concat(
          estateResultItemData?.data.filter(
            (item: Partial<EstateResultItem>): boolean => item.type === EstateResultItemType.Source,
          ) || [],
        ),
      );
      const { openTrades, closedTrades } = fixupTrades(
        estateResultItemData?.data.filter((item: Partial<EstateResultItem>): boolean => {
          return item.type === EstateResultItemType.Trade;
        }),
      );
      setOpenTradeData(openTrades || []);
      setClosedTradeData(closedTrades || []);
      setSuspectedAccountData(
        estateResultItemData?.data.filter((item) => {
          return (
            item.type === EstateResultItemType.Account &&
            item.source !== EstateResultSource.Attorney
          );
        }) || [],
      );
      setConfirmedAccountData(
        estateResultItemData?.data.filter((item) => {
          return (
            item.type === EstateResultItemType.Account &&
            item.source === EstateResultSource.Attorney
          );
        }) || [],
      );
      setTaxTranscriptData(
        estateResultItemData?.data.filter((item) => {
          return (
            item.type === EstateResultItemType.Form_1040 ||
            item.type === EstateResultItemType.Form_1099 ||
            item.type === EstateResultItemType.FormW2
          );
        }) || [],
      );
      setTaxTranscriptEmploymentData(
        estateResultItemData?.data.filter((item) => {
          return item.type === EstateResultItemType.FormW2;
        }) || [],
      );
    }
  }, [estateResultItemData]);

  useEffect(() => {
    context.setContext = setContext;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setShowCreateResultItemModal(context!.isEditingEstateResultItem);
  }, [context]);

  if (
    estateLoading ||
    estateResultItemsLoading ||
    estateShareMembershipLoading ||
    estateResultLoading
  ) {
    return <Spinner />;
  }

  if (hasPortalPermissionClient && !estateShareMembershipData?.data?.isActive) {
    // If shared client user, send to home page if they don't have access to this estate
    history.replace(`/home/${portalId}/home`);
    return <Spinner />;
  }

  const refetchResults = () => {
    refetchEstateResultItems({ estateId: estateId, filter: { visible: true } });
  };

  const now = new Date(Date.now());
  const reportDate = estateResultData?.data?.ccrReportDate
    ? estateResultData.data.ccrReportDate
    : `${now.getMonth() + 1}/${now.getDate()}/${now.getFullYear()}`;

  const content = (
    <div className={classes.parentContainer} style={{ opacity: 1, visibility: 'visible' }}>
      {currentEstateResultItem && (
        <CreatePaymentModal
          onExitClick={() => {
            setCurrentEstateResultItem(undefined);
          }}
          paymentForm={
            <EstateResultItemPaymentForm
              estateResultItem={currentEstateResultItem}
              fullCredits={1}
              onPaymentComplete={refetchResults}
              setShowModal={setShowPaymentModal}
            />
          }
          setShowModal={setShowPaymentModal}
          showModal={showPaymentModal}
        />
      )}
      <Grid container direction="row">
        {(hasPortalPermissionAdmin || hasPortalPermissionUser) && (
          <Grid item>
            <AddResultItemButton onClick={handleAddResultItemClick} />
          </Grid>
        )}
        <Grid item>
          <Button
            component="span"
            variant="contained"
            size="small"
            className={classes.estateDashboardButton}
            onClick={() =>
              history.push(
                `/home/${portalId}/estate/${estateId}/estate-dashboard?tab=${EstateDashboardTab.ESTATE_CHECKLIST}`,
              )
            }
          >
            <WorkOutlineOutlinedIcon className={classes.estateDashboardButtonIcon} />
            Estate Dashboard
          </Button>
        </Grid>
      </Grid>
      <div>
        <p className={classes.reportDate}>Report Date: {reportDate}</p>
      </div>
      <div className={classes.gridContainer}>
        <Grid
          className={classes.gridLeft}
          alignItems="stretch"
          container
          direction="column"
          spacing={2}
        >
          <Grid item>
            <AssetCard
              title="dentity"
              subtitle="Name and Alias History"
              img="/icons/truname-history.svg"
              badgeCount={nameData.length}
              isExpanded={true}
            >
              <Typography className={classes.noData} variant="subtitle2">
                {nameData.length === 0 ? 'None Reported' : ''}
              </Typography>
              {nameData.map((item: Partial<EstateResultItem>) => {
                const name: EstateResultItem_OneViewNameInput = JSON.parse(item.essentialData!);
                return (
                  <NameAsset
                    key={item.id!}
                    id={item.id!}
                    showAssetBtn={false}
                    name={name}
                    title={item.title!}
                  />
                );
              })}
            </AssetCard>
          </Grid>
          <Grid item>
            <AssetCard
              title="ployment"
              subtitle="Current and Former Employers"
              img="/icons/truemployment.svg"
              badgeCount={employmentData.length + taxTranscriptEmploymentData.length}
              isExpanded={true}
            >
              <Typography className={classes.noData} variant="subtitle2">
                {employmentData.length === 0 && taxTranscriptEmploymentData.length === 0
                  ? 'None Reported'
                  : ''}
              </Typography>
              {employmentData.map((item: Partial<EstateResultItem>) => {
                const employment: EstateResultItem_OneViewEmploymentInput = JSON.parse(
                  item.essentialData!,
                );
                return (
                  <EmploymentAsset
                    key={item.id!}
                    id={item.id!}
                    showAssetBtn={false}
                    employment={employment}
                    title={item.title!}
                    subtitle={item.subtitle!}
                  />
                );
              })}
              {taxTranscriptEmploymentData.map((item: Partial<EstateResultItem>) => {
                if (item.type === EstateResultItemType.FormW2) {
                  const transcript: EstateResultItem_TheWorkNumberTaxTranscriptInput = JSON.parse(
                    item.essentialData!,
                  );
                  return (
                    <TranscriptAsset
                      key={item.id!}
                      id={item.id!}
                      showAssetBtn={true}
                      transcript={transcript}
                      title={item.title!}
                      subtitle={item.subtitle!}
                    />
                  );
                }
                return <></>;
              })}
            </AssetCard>
          </Grid>
          <Grid item>
            <AssetCard
              title="property"
              subtitle="Address & Property History"
              img="/icons/truproperty.svg"
              badgeCount={homeAddressData.length + propertyData.length}
              isExpanded={true}
            >
              <Typography
                className={clsx(classes.sublistHeader, {
                  [classes.noData]: homeAddressData.length === 0,
                })}
                variant="subtitle2"
              >
                Home Address History {homeAddressData.length === 0 ? '- None Reported' : ''}
              </Typography>
              {homeAddressData.map((item: Partial<EstateResultItem>) => {
                const address: EstateResultItem_OneViewAddressInput = JSON.parse(
                  item.essentialData!,
                );
                return (
                  <AddressAsset
                    key={item.id!}
                    id={item.id!}
                    showAssetBtn={false}
                    address={address}
                    title={item.title!}
                    subtitle={item.subtitle!}
                  />
                );
              })}
              <Typography
                className={clsx(classes.sublistHeader)}
                // Hide only the property subheader when no data exists
                style={propertyData.length === 0 ? { display: 'none' } : undefined}
                variant="subtitle2"
              >
                Property Ownership History
              </Typography>
              {propertyData.map((item: Partial<EstateResultItem>) => {
                const property: EstateResultItem_PropertyReportInput = JSON.parse(
                  item.essentialData!,
                );
                return (
                  <PropertyAsset
                    id={item.id!}
                    showAssetBtn={true}
                    property={property}
                    title={item.title!}
                    subtitle={item.subtitle!}
                  />
                );
              })}
            </AssetCard>
          </Grid>
        </Grid>
        <Grid
          className={classes.gridCenter}
          alignItems="stretch"
          container
          direction="column"
          spacing={2}
        >
          <Grid item>
            <AssetCard
              title="liabilities"
              subtitle="Bankruptcy, Bills, Trades, and Loans"
              img="/icons/truliabilities.svg"
              badgeCount={
                bankruptcyData.length +
                collectionData.length +
                openTradeData.length +
                closedTradeData.length
              }
              isExpanded={true}
            >
              <div>
                <Typography
                  className={clsx(classes.sublistHeader, {
                    [classes.noData]: bankruptcyData.length === 0,
                  })}
                  variant="subtitle2"
                >
                  Bankruptcy Filing History {bankruptcyData.length === 0 ? '- None Reported' : ''}
                </Typography>
                {bankruptcyData.map((item: Partial<EstateResultItem>) => {
                  const bankruptcy: EstateResultItem_OneViewBankruptcyInput = JSON.parse(
                    item.essentialData!,
                  );
                  return (
                    <BankruptcyAsset
                      id={item.id!}
                      showAssetBtn={true}
                      bankruptcy={bankruptcy}
                      title={item.title!}
                    />
                  );
                })}
              </div>
              <div>
                <Typography
                  className={clsx(classes.sublistHeader, {
                    [classes.noData]: collectionData.length === 0,
                  })}
                  variant="subtitle2"
                >
                  Bills in Collection {collectionData.length === 0 ? '- None Reported' : ''}
                </Typography>
                {collectionData.map((item: Partial<EstateResultItem>) => {
                  const collection: EstateResultItem_OneViewCollectionInput = JSON.parse(
                    item.essentialData!,
                  );
                  return (
                    <CollectionAsset
                      key={item.id!}
                      id={item.id!}
                      showAssetBtn={true}
                      collection={collection}
                      title={item.title!}
                      subtitle={item.subtitle!}
                    />
                  );
                })}
              </div>
              <div>
                <Typography
                  className={clsx(classes.sublistHeader, {
                    [classes.noData]: openTradeData.length === 0,
                  })}
                  variant="subtitle2"
                >
                  Open Loans and Credit Cards {openTradeData.length === 0 ? '- None Reported' : ''}
                </Typography>
                {openTradeData.map((item: Partial<EstateResultItem>) => {
                  const trade: EstateResultItem_OneViewTradeInput = JSON.parse(item.essentialData!);
                  return (
                    <TradeAsset
                      key={item.id!}
                      id={item.id!}
                      showAssetBtn={true}
                      trade={trade}
                      title={item.title!}
                      subtitle={item.subtitle!}
                    />
                  );
                })}
              </div>
              <div>
                <Typography
                  className={clsx(classes.sublistHeader, {
                    [classes.noData]: closedTradeData.length === 0,
                  })}
                  variant="subtitle2"
                >
                  Closed Loans and Credit Cards{' '}
                  {closedTradeData.length === 0 ? '- None Reported' : ''}
                </Typography>
                {closedTradeData.map((item: Partial<EstateResultItem>) => {
                  const trade: EstateResultItem_OneViewTradeInput = JSON.parse(item.essentialData!);
                  return (
                    <TradeAsset
                      key={item.id!}
                      id={item.id!}
                      showAssetBtn={true}
                      trade={trade}
                      title={item.title!}
                      subtitle={item.subtitle!}
                    />
                  );
                })}
              </div>
            </AssetCard>
          </Grid>
        </Grid>
        <Grid
          className={classes.gridRight}
          alignItems="stretch"
          container
          direction="column"
          spacing={2}
        >
          <Grid item>
            <AssetCard
              title="accounts"
              subtitle=""
              img="/icons/truaccounts-and-transactions.svg"
              badgeCount={suspectedAccountData.length + taxTranscriptData.length}
              isExpanded={true}
            >
              <div>
                <Typography
                  className={clsx(classes.sublistHeader, {
                    [classes.noData]: suspectedAccountData.length === 0,
                  })}
                  variant="subtitle2"
                >
                  Suspected Accounts - Please Confirm{' '}
                  {suspectedAccountData.length === 0 ? (
                    '- None Reported'
                  ) : (
                    <div>
                      The below results are inferences drawn from our property and liability data
                      sources. Based on the data received, it may be likely that the individual
                      affiliated with this estate has an account wih the following institutions or
                      had one at some point in the past.
                    </div>
                  )}
                </Typography>
                {suspectedAccountData.map((item: Partial<EstateResultItem>) => {
                  const account: EstateResultItem_AccountInput = JSON.parse(item.essentialData!);
                  return (
                    <AccountAsset
                      key={item.id!}
                      id={item.id!}
                      showAssetBtn={false}
                      account={account}
                      title={item.title!}
                      subtitle={item.subtitle!}
                    ></AccountAsset>
                  );
                })}
              </div>
              <div>
                <Typography
                  className={clsx(classes.sublistHeader, {
                    [classes.noData]:
                      taxTranscriptData.length === 0 && confirmedAccountData.length === 0,
                  })}
                  variant="subtitle2"
                >
                  Confirmed Accounts{' '}
                  {taxTranscriptData.length === 0 && confirmedAccountData.length === 0
                    ? '- None Reported'
                    : ''}
                </Typography>
                {confirmedAccountData.map((item: Partial<EstateResultItem>) => {
                  const account: EstateResultItem_AccountInput = JSON.parse(item.essentialData!);
                  return (
                    <AccountAsset
                      key={item.id!}
                      id={item.id!}
                      showAssetBtn={true}
                      account={account}
                      title={item.title!}
                      subtitle={item.subtitle!}
                    ></AccountAsset>
                  );
                })}
                {taxTranscriptData.length ? (
                  taxTranscriptData.map((item: Partial<EstateResultItem>) => {
                    const transcript: EstateResultItem_TheWorkNumberTaxTranscriptInput = JSON.parse(
                      item.essentialData!,
                    );
                    return (
                      <TranscriptAsset
                        key={item.id!}
                        id={item.id!}
                        showAssetBtn={true}
                        transcript={transcript}
                        title={item.title!}
                        subtitle={item.subtitle!}
                      ></TranscriptAsset>
                    );
                  })
                ) : estateData?.data.twnDate ? ( // show pending if the tax transcript has been requested
                  <TwnMessagePanel />
                ) : (
                  ''
                )}
              </div>
            </AssetCard>
          </Grid>
          <Grid item>
            <AssetCard
              title="source"
              subtitle={'Data Source Information'}
              badgeCount={undefined}
              isExpanded={false}
            >
              {sourceData.map((item: Partial<EstateResultItem>) => {
                const source: EstateResultItem_OneViewSourceInfoInput = JSON.parse(
                  item.essentialData!,
                );
                return (
                  <SourceAsset
                    key={item.id!}
                    id={item.id!}
                    showAssetBtn={true}
                    source={source}
                    title={item.title!}
                  />
                );
              })}
            </AssetCard>
          </Grid>
        </Grid>
      </div>
      <AddOrEditResultItemModal
        showModal={showCreateResultItemModal}
        setShowModal={setShowCreateResultItemModal}
        onExitClick={() => {
          setShowCreateResultItemModal(false);
        }}
        refetchResults={refetchResults}
      />
    </div>
  );

  return (
    <EstateResultsPageContext.Provider value={context}>
      {hideNav ? content : <HomeLayout>{content}</HomeLayout>}
    </EstateResultsPageContext.Provider>
  );
}
